import React, { useState, useMemo, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import shortid from 'shortid'
import classNames from 'classnames'
import { Link } from 'react-router-dom'

import SiteWrapper from '../components/sitewrapper'
import Layout from '../components/layout'
import Manage from '../components/dashboard-manage'
import Intro from '../components/intro'
import Button, { ButtonRow } from '../components/button'
import styles from '../component-styles/dashboard.module.scss'
import HeaderPanel, { Panel } from '../components/header-panel'
import { getItemByKeyValue } from '../helpers'
import Tag from '../components/tag'
import Stars from '../assets/star-cluster-yellow.svg'
import { getRemoteDashboardList } from '../store/actions'
import ButtonTabs from '../components/button-tabs'

interface EditProps {
  onClick: () => void
  isEdit: boolean
}

export function EditButton({ onClick, isEdit }: EditProps): React.ReactElement {
  return (
    <>
      <Button
        type="flatBlue"
        className={styles.manageButton}
        onClick={(): void => {
          onClick()
        }}
      >
        {!isEdit ? 'Add/Edit' : 'Add/Edit'}
      </Button>
    </>
  )
}

export default function DashboardPage(): React.ReactElement {
  const [manageActive, setActive] = useState(false)
  const [activeId, setActiveId] = useState('')
  const [embedHelpVisible, setEmbedHelpVisible] = useState(false)

  const reports = useSelector((s: any): any => s.reports)
  const { dashboards, loading } = reports
  const dispatch = useDispatch()

  useEffect((): void => {
    if (!dashboards) {
      dispatch(getRemoteDashboardList())
    }
    // eslint-disable-next-line
  }, [])

  const onClickHeaderButton = (currentID: string) => {
    setActiveId(currentID)
  }

  const currentEmbedItem = useMemo(() => {
    if (dashboards && dashboards.length > 0) {
      if (activeId === '') {
        return { ...dashboards[0] }
      }

      let item = getItemByKeyValue(dashboards, 'dashboardID', activeId)
      if (item === -1) {
        item = { ...dashboards[0] }
        setActiveId(item.dashboardID)
      }
      return item
    }
    return null
  }, [activeId, dashboards])

  return (
    <SiteWrapper grey>
      <Layout width={1400}>
        <Intro title="Dashboards">
          <p>
            View, share and discuss embedded dashboards here.{' '}
            {!loading && dashboards && dashboards.length > 0 && (
              <>
                <Button
                  type="link"
                  onClick={() => setEmbedHelpVisible(!embedHelpVisible)}
                >
                  How to embed dashboards
                </Button>
                .
              </>
            )}
          </p>
          {((!loading && dashboards && dashboards.length === 0) ||
            embedHelpVisible) && (
            <Tag multiLine icon={Stars}>
              <ButtonTabs
                label="To embed your dashboard from:"
                tabsLabels={['Google Data Studio', 'PowerBI', 'Other']}
              >
                <>
                  <ol className={styles.numberedList}>
                    <li>
                      Login to data studio and navigate to{' '}
                      <a
                        href="https://datastudio.google.com/navigation/reporting"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        datastudio.google.com/navigation/reporting
                      </a>
                    </li>
                    <li>Open the dashboard that you would like to share.</li>
                    <li>
                      Click the blue <strong>'Edit'</strong> button in the
                      top-right.
                    </li>
                    <li>
                      Click the <strong>'Share'</strong> button in the
                      top-right.
                    </li>
                    <li>
                      In the manage access tab, select{' '}
                      <strong>'Anyone with this link can view'</strong>.{' '}
                    </li>
                    <li>
                      Close this pop up and select{' '}
                      <strong>'Embed report'</strong> from the dropdown list by
                      the share button.
                    </li>
                    <li>
                      Click the checkbox to <strong>'Enable embedding'</strong>,
                      and copy the value for <strong>'Embed URL'</strong>.
                    </li>
                    <li>
                      Navigate to{' '}
                      <Link
                        className={styles.backButton}
                        to="/report/dashboards"
                      >
                        uplifter.tech/report/dashboards
                      </Link>{' '}
                      and click on the <strong>'Add/Edit'</strong> button.{' '}
                    </li>
                    <li>
                      Click on <strong>'Add a new dashboard +'</strong>, add a
                      dashboard name and paste the embed link.
                    </li>
                    <li>
                      Click <strong>'Save'</strong>.
                    </li>
                  </ol>
                </>

                <>
                  <ol className={styles.numberedList}>
                    <li>
                      Log in to the online PowerBI service:{' '}
                      <a
                        href="https://app.powerbi.com/home"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        app.powerbi.com/home
                      </a>
                    </li>
                    <li>Navigate to the workspace which holds your report.</li>
                    <li>
                      Currently Uplifter only supports public reports that are
                      shared to the web. Click the report that you would like to
                      embed.
                    </li>
                    <li>
                      In the top panel, there is an option for{' '}
                      <strong>'Share'</strong>. Click this option and click{' '}
                      <strong>'Publish to Web'</strong>.
                    </li>
                    <li>
                      This will open a pop-up box that should say{' '}
                      <strong>'Embed Code'</strong> and contain an image of your
                      report.
                    </li>
                    <li>
                      Copy the link in the{' '}
                      <strong>'Link you can send in email'</strong> box.
                    </li>
                    <li>
                      Navigate to{' '}
                      <Link
                        className={styles.backButton}
                        to="/report/dashboards"
                      >
                        uplifter.tech/report/dashboards
                      </Link>{' '}
                      and click on the <strong>'Add/Edit'</strong> button.
                    </li>
                    <li>
                      Click on <strong>'Add a new dashboard +'</strong> to add a
                      dashboard name and the dashboard embed link you copied
                      from PowerBI.
                    </li>
                  </ol>
                  <p className={styles.note}>
                    You may get a warning when you click{' '}
                    <strong>'Publish to web'</strong>. In this case, an
                    administrator for your Microsoft account must enable{' '}
                    <strong>'Publish to web'</strong> for your organisation.
                    Direct your administrator to this link to enable{' '}
                    <strong>'Allow existing and new codes'</strong> within the
                    PowerBI admin panel:{' '}
                    <a
                      href="https://docs.microsoft.com/en-us/power-bi/admin/service-admin-portal#publish-to-web"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      docs.microsoft.com/en-us/power-bi/admin/service-admin-portal#publish-to-web
                    </a>
                  </p>
                </>
                <p>
                  Looking for help, or looking to embed dashboards from other
                  providers? Email{' '}
                  <a href="mailto:support@uplifter.ai">support@uplifter.ai</a>{' '}
                  and let's talk!
                </p>
              </ButtonTabs>
            </Tag>
          )}
        </Intro>

        <HeaderPanel>
          <Panel>
            <ButtonRow>
              {dashboards &&
                dashboards.map(({ dashboardName, dashboardID }, index) => {
                  const active = activeId === dashboardID

                  return (
                    <Button
                      type="text"
                      key={shortid.generate()}
                      className={classNames(styles.tabButton, {
                        [styles.tabButtonActive]:
                          activeId === '' && index === 0 ? true : active,
                      })}
                      onClick={(): void => {
                        onClickHeaderButton(dashboardID)
                      }}
                    >
                      {dashboardName}
                    </Button>
                  )
                })}
              {dashboards && (
                <>
                  {dashboards.length > 0 && <b className={styles.divider} />}
                  <EditButton
                    onClick={(): void => {
                      setActive(true)
                    }}
                    isEdit={dashboards.length > 0}
                  />
                </>
              )}
            </ButtonRow>
          </Panel>
        </HeaderPanel>

        {currentEmbedItem && (
          <div key={currentEmbedItem.id}>
            <div className={styles.embededIFrameWrapper}>
              <iframe
                title={currentEmbedItem.dashboardName}
                className={styles.embededIFrame}
                src={currentEmbedItem.embedLink}
              />
            </div>
          </div>
        )}
      </Layout>
      <Manage
        active={manageActive}
        data={dashboards}
        toggleActive={(): void => setActive(!manageActive)}
      />
    </SiteWrapper>
  )
}
